import { PrivateContext } from "@/routes/PrivateRoute";
import { useContext, useState, useEffect } from "@/common/hooks";
import {
  useGetAllInvestmentVehiclesForProductQuery,
  useGetInvestmentCompaniesQuery,
  useGetInvestmentVehiclesForProductQuery,
  useProductInvestmentVehiclesAuditMutation,
  useSaveProductInvestmentVehiclesMutation,
} from "@/service/ProductService";
import {
  AgGrid,
  ErrorMessage,
  SelectDropdown,
  Spinner,
  Button,
  InputField,
  CommonLink,
  Heading,
  InfoBox,
  SuccessPopup,
  MobileResponsiveGrid,
} from "@/common/components";
import {
  getCurrentDate,
  getFormattedDate,
  getFormattedDateTime,
  scrollToTop,
} from "@/utils/Utilities";
import { useGetRecordKeepingSystemQuery } from "@/service/AdminService";

const InvestmentVehicles = () => {
  const {
    labels,
    setNavHeader,
    setPageHeader,
    location,
    remove,
    cloneDeep,
    cancel,
    sortBy,
    isEqual,
    setNavCancelMessage,
    hasAccessToUpdate,
    accessorPrivileges,
  } = useContext(PrivateContext);

  const [investmentCompanies, setInvestmentCompanies] = useState([]);
  const [recordKeepers, setRecordKeepers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [rowDataForAll, setRowDataForAll] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [recordKeeperId, setRecordKeeperId] = useState("");
  const [externalId, setExternalId] = useState("");
  const [showHistoryGrid, setShowHistoryGrid] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [maxResults, setMaxResults] = useState(100);
  const [selectedVehicleFromAll, setSelectedVehicleFromAll] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [errors, setErrors] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const defaultColDef = {
    cellClass: "mt-1",
  };

  const hasAccessToUpdateInvestmentVehicles = () => {
    if (hasAccessToUpdate()) {
      return true;
    } else {
      return (
        accessorPrivileges["PRODUCT"] == "ADD" ||
        accessorPrivileges["PRODUCT"] == "UPDATE"
      );
    }
  };

  const columnDefsForAll = [
    {
      field: "investmentManagementCompanyName",
      headerName: labels?.productInvestmentVehicle.investmentManagementCompany,
    },
    {
      field: "name",
      headerName: labels?.productInvestmentVehicle.name,
    },
    {
      field: "externalId",
      headerName: labels?.productInvestmentVehicle.externalId,
    },
    {
      field: "tradingSymbol",
      headerName: labels?.productInvestmentVehicle.tradingSymbol,
    },
    {
      field: "targetFundActivationDate",
      headerName: labels?.productInvestmentVehicle.targetFundActivationDate,

      cellRenderer: getFormattedDate,
    },
  ];

  const [columnDefs, setColumnDefs] = useState([
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      flex: 0.25,
    },
    {
      field: "investmentVehicle.investmentManagementCompanyName",
      headerName: labels?.productInvestmentVehicle.investmentManagementCompany,
      flex: 1.75,
    },
    {
      field: "investmentVehicle.name",
      headerName: labels?.productInvestmentVehicle.name,
    },
    {
      field: "investmentVehicle.externalId",
      headerName: labels?.productInvestmentVehicle.externalId,
    },
    {
      field: "externalVehicleId",
      headerName: labels?.productInvestmentVehicle.externalVehicleIdHeader,
    },
    {
      field: "investmentVehicle.tradingSymbol",
      headerName: labels?.productInvestmentVehicle.tradingSymbol,
    },
    {
      field: "recordkeepingSystemName",
      headerName: labels?.productInvestmentVehicle.recordKeeperGrid,
      flex: 1.75,
    },
    {
      field: "activeDate",
      headerName: labels?.productInvestmentVehicle.activeDate,
    },
    {
      field: "investmentVehicle.targetFundActivationDate",
      headerName: labels?.productInvestmentVehicle.targetFundActivationDate,
    },
  ]);

  const historyDefs = [
    {
      field: "lastMntDateTime",
      headerName: labels?.productInvestmentVehicle.date,
      cellRenderer: getFormattedDateTime,
      flex: 2,
    },
    {
      field: "investmentVehicleName",
      headerName: labels?.productInvestmentVehicle.investmentVehicleName,
    },
    {
      field: "externalVehicleId",
      headerName: labels?.productInvestmentVehicle.externalVehicleIdForHistory,
    },
    {
      field: "recordkeeperName",
      headerName:
        labels?.productInvestmentVehicle
          .recordKeeperForInvestmentVehicleHistory,
    },
    {
      field: "activeDate",
      headerName: labels?.productInvestmentVehicle.activeDate,

      cellRenderer: getFormattedDate,
    },
    {
      field: "closedDate",
      headerName: labels?.productInvestmentVehicle.closedDate,

      cellRenderer: getFormattedDate,
    },
    {
      field: "lastMntOpId",
      headerName: labels?.productInvestmentVehicle.operatorID,
    },
  ];

  const {
    data: investmentCompaniesData,
    isFetching,
    error,
  } = useGetInvestmentCompaniesQuery(location?.state?.productSponsorId);

  const {
    data: investmentVehicles,
    isFetching: isFetching1,
    error: error1,
  } = useGetInvestmentVehiclesForProductQuery({
    productSponsorId: location?.state?.productSponsorId,
    productId: location?.state?.productId,
  });

  const {
    data: allInvestmentVehicles,
    isFetching: isFetching2,
    error: error2,
  } = useGetAllInvestmentVehiclesForProductQuery(
    location?.state?.productSponsorId
  );

  const {
    data: recordKeepersData,
    isFetching: isFetching3,
    error: error3,
  } = useGetRecordKeepingSystemQuery();

  const [getVehicleHistoryApi, { isLoading: isFetching4, error: error4 }] =
    useProductInvestmentVehiclesAuditMutation();

  const [saveVehiclesApi, { isLoading: isFetching5, error: error5 }] =
    useSaveProductInvestmentVehiclesMutation();

  const onRowClickedForAll = (e) => {
    setSelectedVehicleFromAll(e.data.id);
  };

  const getRowClassForAll = (params) => {
    if (params.data.id === selectedVehicleFromAll) {
      return "hover-bg";
    }
    return "";
  };

  const onSelectionChanged = (e) => {
    const selectedRows = e.api.getSelectedRows();
    setSelectedVehicle([...selectedRows]);
  };

  const getRecordKeeperName = (id) => {
    const data = recordKeepers.find((e) => e.id === id);
    return data?.recordkeeperName + " - " + data?.recordkeepingName;
  };

  const getInvestmentCompanyName = (id) => {
    return investmentCompanies.find((e) => e.id === id)?.name;
  };

  function areAuditRowsEqual(a, b) {
    return (
      a.investmentVehicleName === b.investmentVehicleName &&
      a.externalVehicleId === b.externalVehicleId &&
      a.recordkeeperName === b.recordkeeperName &&
      a.activeDate === b.activeDate &&
      a.closedDate === b.closedDate
    );
  }

  function filterDuplicateAuditRows(dataArray) {
    const result = [];

    for (let i = 0; i < dataArray.length; i++) {
      const currentItem = dataArray[i];
      const nextItem = dataArray[i + 1];

      if (!nextItem || !areAuditRowsEqual(currentItem, nextItem)) {
        result.push(currentItem);
      }
    }

    return result;
  }

  const getAuditData = () => {
    getVehicleHistoryApi({
      productSponsorId: location?.state?.productSponsorId,
      productId: location?.state?.productId,
      firstResult: 0,
      maxResults: maxResults,
    })
      .unwrap()
      .then((res) => {
        if (res?.moreRecords === "true") {
          setMaxResults(maxResults + 100);
        } else {
          setMaxResults(0);
        }
        const data = cloneDeep(
          res?.productInvestmentVehicleAuditPageDTOBeans
        ).sort((a, b) => {
          return b.lastMntDateTime - a.lastMntDateTime;
        });

        setHistoryData(filterDuplicateAuditRows(data));
      });
  };

  const hasValidNetworkConfiguration = () => {
    const rk = recordKeepers.find((e) => e.id === recordKeeperId);
    return rk?.serverHostName || rk?.hostPortNumber;
  };

  const isSelectedVehicleDuplicate = (vehicle) => {
    for (let i = 0; i < rowData.length; i++) {
      if (
        vehicle.id === rowData[i]?.investmentVehicleId &&
        recordKeeperId === rowData[i]?.recordkeepingSystemId
      ) {
        return true;
      }
    }
    return false;
  };

  const isExternalVehicleIdDuplicate = () => {
    for (let i = 0; i < rowData.length; i++) {
      if (
        recordKeeperId === rowData[i]?.recordkeepingSystemId &&
        externalId === rowData[i]?.externalVehicleId
      ) {
        return true;
      }
    }
    return false;
  };

  const findCommonProductInvestmentVehicle = (id) => {
    for (let i = 0; i < rowData.length; i++) {
      if (id === rowData[i]?.investmentVehicleId) {
        return rowData[i];
      }
    }
    return null;
  };

  const addNewVehicle = () => {
    setErrors(null);

    if (hasValidNetworkConfiguration()) {
      let activeDate = "";
      let isRemovable = false;
      const vehicle = rowDataForAll.find(
        (e) => e.id === selectedVehicleFromAll
      );

      if (!isSelectedVehicleDuplicate(vehicle)) {
        if (!isExternalVehicleIdDuplicate()) {
          const commonProductInvestmentVehicle =
            findCommonProductInvestmentVehicle(vehicle.id);

          if (commonProductInvestmentVehicle != null) {
            activeDate = commonProductInvestmentVehicle.activeDate;
            isRemovable = commonProductInvestmentVehicle.isRemovable;
          } else {
            activeDate = getCurrentDate();
            isRemovable = true;
          }

          const data = [
            ...rowData,
            {
              activeDate: activeDate,
              recordkeepingSystemId: recordKeeperId,
              recordkeepingSystemName: getRecordKeeperName(recordKeeperId),
              externalVehicleId: externalId,
              investmentVehicleId: vehicle.id,
              investmentManagementCompanyName:
                vehicle.investmentManagementCompanyName,
              investmentVehicle: vehicle,
              productSponsorId: location?.state?.productSponsorId,
              productId: location?.state?.productId,
              isRemovable: isRemovable,
            },
          ];

          setRowData(sortBy(data, "investmentManagementCompanyName"));
        } else {
          setErrors({
            errorMessage:
              labels?.productInvestmentVehicle?.duplicateExternalIdMsg,
          });
          scrollToTop();
          return;
        }
      } else {
        setErrors({
          errorMessage: labels?.productInvestmentVehicle?.duplicateInvMsg,
        });
        scrollToTop();
        return;
      }
    } else {
      setErrors({
        errorMessage: labels?.productInvestmentVehicle?.rkConfigMsg,
      });
      scrollToTop();
      return;
    }
  };

  const removeVehicle = () => {
    const data = cloneDeep(rowData);

    for (let i = 0; i < selectedVehicle.length; i++) {
      if (selectedVehicle[i].isRemovable === "false") {
        setErrors({
          errorMessage: labels?.productInvestmentVehicle?.removeMsg,
        });
        scrollToTop();
        return;
      }
    }

    selectedVehicle.forEach((e) => {
      remove(data, (item) => isEqual(item, e));
    });
    setRowData(data);
  };

  const updateVehicle = () => {
    scrollToTop();

    const payload = {
      productSponsorId: location?.state?.productSponsorId,
      productId: location?.state?.productId,
      productInvestmentVehicles: rowData,
    };

    saveVehiclesApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  useEffect(() => {
    if (rowDataForAll.length) {
      setSelectedVehicleFromAll(rowDataForAll[0]?.id);
    }
  }, [rowDataForAll]);

  useEffect(() => {
    if (selectedCompany) {
      setSelectedVehicleFromAll(
        rowDataForAll.filter(
          (e) => e.investmentManagementCompanyId === selectedCompany
        )[0]?.id
      );
    } else {
      setSelectedVehicleFromAll(rowDataForAll[0]?.id);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (
      investmentCompaniesData &&
      investmentCompaniesData?.investmentManagementCompanys
    ) {
      setInvestmentCompanies(
        investmentCompaniesData?.investmentManagementCompanys
      );
    }
  }, [investmentCompaniesData]);

  useEffect(() => {
    if (
      recordKeepersData &&
      recordKeepersData?.recordkeeperRecordkeepingSystems
    ) {
      let tempData = [];
      recordKeepersData?.recordkeeperRecordkeepingSystems?.forEach(
        (element) => {
          const newElement = {
            ...element,
            newRecordkeeperName: `${element.recordkeeperName ?? ""} - ${
              element.recordkeepingName ?? ""
            }`,
          };
          tempData.push(newElement);
        }
      );
      setRecordKeepers(tempData);
      setRecordKeeperId(
        recordKeepersData?.recordkeeperRecordkeepingSystems[0]?.id
      );
    }
  }, [recordKeepersData]);

  useEffect(() => {
    if (allInvestmentVehicles && allInvestmentVehicles?.investementVehicles) {
      setRowDataForAll(
        cloneDeep(
          sortBy(
            allInvestmentVehicles?.investementVehicles.filter(
              (e) => !e.closedDate
            ),
            "investmentManagementCompanyName"
          )
        )
      );
    }
  }, [allInvestmentVehicles]);

  useEffect(() => {
    if (
      investmentVehicles &&
      investmentVehicles?.productInvestmentVehicles &&
      investmentCompanies.length &&
      recordKeepers.length
    ) {
      let data = cloneDeep(investmentVehicles?.productInvestmentVehicles);
      data.forEach((e) => {
        e.recordkeepingSystemName = getRecordKeeperName(
          e.recordkeepingSystemId
        );
        e.investmentVehicle.investmentManagementCompanyName =
          getInvestmentCompanyName(e.investmentVehicle.imcId);
      });
      setRowData(data);
    }
  }, [investmentCompanies, recordKeepers, investmentVehicles]);

  useEffect(() => {
    setNavHeader(labels?.productInvestmentVehicle?.headerName);
    setPageHeader(
      labels?.productInvestmentVehicle?.investmentVehiclesFor +
        location?.state?.name
    );
    setNavCancelMessage(labels?.productInvestmentVehicle?.cancelMessage);
    getAuditData();
    if (!hasAccessToUpdateInvestmentVehicles()) {
      remove(columnDefs, (e) => e.headerCheckboxSelection === true);
      setColumnDefs([...columnDefs]);
    }
  }, []);

  return (
    <section className="container-x-large py-4">
      {(isFetching ||
        isFetching1 ||
        isFetching2 ||
        isFetching3 ||
        isFetching4 ||
        isFetching5) && <Spinner />}
      {rowDataForAll.length == 0 ? (
        <div>{labels?.productInvestmentVehicle?.noAllVehicleData}</div>
      ) : (
        <>
          {hasAccessToUpdateInvestmentVehicles() && (
            <InfoBox
              message={labels?.productInvestmentVehicle?.selectInvCompanyMsg}
              wrapperClassName="mb-4"
            />
          )}

          <ErrorMessage
            error={
              errors || error || error1 || error2 || error3 || error4 || error5
            }
            wrapperClassName="mb-3"
          />

          {hasAccessToUpdateInvestmentVehicles() && (
            <>
              <SelectDropdown
                id="managementCompaniesSelectedByProduct"
                label={
                  labels?.productInvestmentVehicle.investmentManagementCompany
                }
                dropdownData={investmentCompanies}
                wrapperClassName="col-md-4 mb-2"
                selectClassName="w-100 py-2"
                select={(e) => setSelectedCompany(e)}
                value={selectedCompany}
                optionId="id"
                optionLabel="name"
                defaultOption={true}
                defaultOptionLabel={"ALL"}
                ariaLabel="Management Company"
              />
              <AgGrid
                printClassNames="mt-md-n3"
                wrapperClassNames="d-md-block d-none"
                defaultColDef={defaultColDef}
                rowData={
                  selectedCompany
                    ? rowDataForAll.filter(
                        (e) =>
                          e.investmentManagementCompanyId === selectedCompany
                      )
                    : rowDataForAll
                }
                columnDefs={columnDefsForAll}
                domLayout="normal"
                getRowClass={getRowClassForAll}
                onRowClicked={onRowClickedForAll}
              />
              <MobileResponsiveGrid
                key="productInvestmentVehicles"
                rowData={
                  selectedCompany
                    ? rowDataForAll.filter(
                        (e) =>
                          e.investmentManagementCompanyId === selectedCompany
                      )
                    : rowDataForAll
                }
                columnDefs={columnDefsForAll}
                getRowClass={getRowClassForAll}
                onRowClick={onRowClickedForAll}
              />
            </>
          )}

          {hasAccessToUpdateInvestmentVehicles() && (
            <div className="d-md-flex align-items-end mt-3 mb-5">
              <SelectDropdown
                id="recordKeeper"
                dropdownData={recordKeepers}
                selectClassName="w-100 py-2"
                wrapperClassName="col-md-5 me-md-3"
                select={(e) => setRecordKeeperId(e)}
                value={recordKeeperId}
                optionId="id"
                optionLabel="newRecordkeeperName"
                label={labels?.productInvestmentVehicle?.recordKeeper}
                ariaLabel="Record Keeper"
              />

              <InputField
                id="externalVehicleId"
                type="text"
                label={labels?.productInvestmentVehicle?.externalVehicleId}
                wrapperClassName="col-md-3 me-md-3"
                inputClass="py-2"
                maxLength="20"
                change={(e) => setExternalId(e.replace(/\s+/g, ""))}
                value={externalId.replace(/\s+/g, "")}
                blur={(e) => setExternalId(e.replace(/\s+/g, ""))}
              />

              <Button
                id="addExternalVehicleId"
                wrapperClassName="form-group col-md-2"
                text={labels?.productInvestmentVehicle.addNewVehicle}
                click={addNewVehicle}
                disabled={!externalId || !selectedVehicleFromAll}
              />
            </div>
          )}

          <>
            {hasAccessToUpdateInvestmentVehicles() && (
              <Heading
                text={`${labels?.productInvestmentVehicle?.invVehicleFor} ${location?.state?.name}`}
                wrapperClassNames="h4 border-bottom-1 pb-2"
                title={false}
              />
            )}
            <AgGrid
              showPrintTable={false}
              agGridClassNames="w-1400"
              rowData={rowData}
              columnDefs={columnDefs}
              wrapperClassNames="mb-4 d-none d-md-block"
              domLayout="normal"
              rowSelection="multiple"
              onSelectionChanged={onSelectionChanged}
              overlayNoRowsTemplate={labels?.productInvestmentVehicle?.noData}
            />
            <MobileResponsiveGrid
              rowData={rowData}
              columnDefs={columnDefs}
              enableCheckboxSelection={true}
              onSelectionChanged={(selectedRows) => {
                setSelectedVehicle([...selectedRows]);
              }}
              noDataLabel={labels?.productInvestmentVehicle?.noData}
            />
            {hasAccessToUpdateInvestmentVehicles() && (
              <div className="d-flex justify-content-end mt-2 mb-5 flex-md-row flex-column-reverse row-gap-1">
                <Button
                  id="remove"
                  text={labels?.productInvestmentVehicle?.remove}
                  wrapperClassName="col-md-2 me-md-3"
                  click={removeVehicle}
                  disabled={!selectedVehicle.length}
                />

                <Button
                  id="done"
                  text={labels?.productInvestmentVehicle?.submit}
                  wrapperClassName="col-md-2 mb-2"
                  click={updateVehicle}
                />
              </div>
            )}
          </>

          <>
            <Heading
              text={labels?.productInvestmentVehicle?.invHistory}
              wrapperClassNames="h4 border-bottom-1 pb-2"
              title={false}
            />

            <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
              <CommonLink wrapperClassName="d-flex justify-content-end mb-2">
                <span onClick={() => setShowHistoryGrid(!showHistoryGrid)}>
                  {`${
                    !showHistoryGrid
                      ? labels?.productInvestmentVehicle?.show
                      : labels?.productInvestmentVehicle?.hide
                  } ${labels?.productInvestmentVehicle?.auditHistory}`}
                </span>
              </CommonLink>
            </div>

            {showHistoryGrid && (
              <>
                <AgGrid
                  wrapperClassNames="pb-5 d-md-block d-none"
                  columnDefs={historyDefs}
                  rowData={historyData}
                  domLayout="normal"
                  overlayNoRowsTemplate={
                    labels?.productInvestmentVehicle?.noVehiclesHistory
                  }
                >
                  {maxResults ? (
                    <CommonLink wrapperClassName="d-flex justify-content-end mt-2">
                      <span onClick={() => getAuditData()}>
                        {labels?.productInvestmentVehicle?.more}
                      </span>
                    </CommonLink>
                  ) : (
                    ""
                  )}
                </AgGrid>
                <MobileResponsiveGrid
                  rowData={historyData}
                  columnDefs={historyDefs}
                  noDataLabel={
                    labels?.productInvestmentVehicle?.noVehiclesHistory
                  }
                >
                  {maxResults ? (
                    <CommonLink wrapperClassName="d-flex justify-content-end my-2">
                      <span onClick={() => getAuditData()}>
                        {labels?.productInvestmentVehicle?.more}
                      </span>
                    </CommonLink>
                  ) : (
                    ""
                  )}
                </MobileResponsiveGrid>
              </>
            )}
          </>

          <SuccessPopup
            showModal={showPopup}
            buttonText={labels?.productInvestmentVehicle?.continue}
            click={cancel}
            body={labels?.productInvestmentVehicle?.requestCompleted}
          />
        </>
      )}
    </section>
  );
};

export default InvestmentVehicles;
